import { css } from '@emotion/react';
import { useMemo } from 'react';
import { AutocompleteComponent } from '~/_shared/baseComponents/autocomplete';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { SwitchOptionsComponent } from '~/_shared/components/switchOptions/switchOptions.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { ContinueButton } from '~/map/layered/createLayeredMapModal/ContinueButton';
import {
  BuildingBlockSelections,
  BuildingBlockSubsetTypes,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { type BoundaryId } from '~/store/boundaries/boundaryIdentifier.type';

// From data option si currently disabled thus the entire selector is not shown
const SUBSET_TYPE_SELECTION_ENABLED = false;

const rowGap = 20;
const gridContainerStyle = ({ showSubset }: { showSubset: boolean }) => css({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gridTemplateAreas: `
    'blockTypeLabel blockTypeLabel'
    'blockType subset'
    ${!showSubset ? `
    'excludeAreas excludeAreas'
    ` : ''}
    ${showSubset ? `
      'subsetLabel subsetLabel'
      ${SUBSET_TYPE_SELECTION_ENABLED ? '\'subsetSelect goToMap\'' : '\'goToMap goToMap\''}
    ` : ''}
    'selectedBlocks continue'
  `,
  alignItems: 'center',
  justifyItems: 'start',
  gap: `${rowGap}px 15px`,
});

const labelStyle = css({
  marginBottom: 5 - rowGap,
});

const checkboxWrapperStyle = css({
  gridArea: 'excludeAreas',
});

const rowLabel = css({
  marginLeft: 10,
  lineHeight: 1.25,
});

const checkboxTextStyle = css({
  textTransform: 'uppercase',
});

const checkboxAdditionalTextStyle = (theme: Theme) => css({
  color: theme.textColors.tertiary,
});

export type BuildingBlockStepComponentProps<T> = Readonly<{
  value: T | null;
  options: ReadonlyArray<DropdownOption<T>>;
  buildingBlockSelection: BuildingBlockSelections;
  buildingBlockSubsetType: string;
  selectedBoundaryIds?: ReadonlyArray<BoundaryId>;
  excludeRemoteAreas?: boolean;

  onChange: (value: T) => void;
  onExcludeRemoteAreasChange?: (value: boolean) => void;
  onContinue: () => void;
  onSelectSubsetFromMap: () => void;
  onChangeBuildingBlockSelection: (value: BuildingBlockSelections) => void;
  onChangeBuildingBlockSubsetType: (value: string) => void;
}>;

export const BuildingBlockStepComponent = <T, >({
  value,
  excludeRemoteAreas,
  options,
  onChange,
  onContinue,
  onSelectSubsetFromMap,
  buildingBlockSelection,
  buildingBlockSubsetType,
  selectedBoundaryIds,
  onChangeBuildingBlockSelection,
  onChangeBuildingBlockSubsetType,
  onExcludeRemoteAreasChange,
}: BuildingBlockStepComponentProps<T>) => {
  const [t] = useTranslation();

  const subsetOptions = useMemo<{
    name: string;
    value: string;
  }[]>(() => ([
    {
      name: t('boundaryAI.On Map'),
      value: BuildingBlockSubsetTypes.ON_MAP_BY_LASSO,
    },
    {
      name: t('boundaryAI.From Data'),
      value: BuildingBlockSubsetTypes.FROM_DATA,
    },
  ]), [t]);

  const isManualSelectionRequired = useMemo(() =>
    !value || (
      buildingBlockSelection === BuildingBlockSelections.SUBSET &&
      buildingBlockSubsetType !== BuildingBlockSubsetTypes.FROM_DATA &&
      !selectedBoundaryIds?.length
    ), [value, buildingBlockSubsetType, buildingBlockSelection, selectedBoundaryIds],
  );

  const isSubmitDisabled = !value || isManualSelectionRequired;
  const showSubset = buildingBlockSelection === BuildingBlockSelections.SUBSET;
  const blockTypeLabel = options.find(o => o.value === value)?.name;

  return (
    <div css={gridContainerStyle({ showSubset })}>
      <span css={[{ gridArea: 'blockTypeLabel' }, labelStyle]}>{t('boundaryAI.Building Block Type')}</span>
      <AutocompleteComponent
        css={[{ gridArea: 'blockType', justifySelf: 'stretch' }]}
        value={value}
        onChange={onChange}
        multiple={false}
        options={options}
        inPortal
      />
      <SwitchOptionsComponent
        css={{ gridArea: 'subset', justifySelf: 'end' }}
        isDisabled={!value}
        options={[{
          value: BuildingBlockSelections.ALL,
          label: t('all').toUpperCase(),
          tooltip: blockTypeLabel && t('boundaryAI.Include all {{blocks}} for the territory generation',
            { blocks: blockTypeLabel }),
        }, {
          value: BuildingBlockSelections.SUBSET,
          label: t('boundaryAI.A subset of').toUpperCase(),
          tooltip: blockTypeLabel && t('boundaryAI.Include only a subset of {{blocks}} for the territory generation',
            { blocks: blockTypeLabel }),
        }]}
        value={buildingBlockSelection}
        onChange={onChangeBuildingBlockSelection}
      />

      {!showSubset && onExcludeRemoteAreasChange && (
        <div css={checkboxWrapperStyle}>
          <CheckboxComponent
            isChecked={!!excludeRemoteAreas}
            checkedSetter={() => onExcludeRemoteAreasChange(!excludeRemoteAreas)}
            text={(
              <div>
                <div css={checkboxTextStyle}>{t('Exclude Remote Areas (e.g., Alaska, Hawaii)')}</div>
                <div css={checkboxAdditionalTextStyle}>({t('Only use the largest connected region for territories')})</div>
              </div>
            )}
            textStyle={rowLabel}
          />
        </div>
      )}

      {showSubset && (
        <>
          <span css={[{ gridArea: 'subsetLabel' }, labelStyle]}>
            {SUBSET_TYPE_SELECTION_ENABLED ?
              t('boundaryAI.Choose Subset Selection Method') :
              t('boundaryAI.Select the subset area on map')}
          </span>
          {SUBSET_TYPE_SELECTION_ENABLED && (
            <div>
              <RegularDropdownComponent
                css={{ gridArea: 'subsetSelect', justifySelf: 'stretch' }}
                onChange={onChangeBuildingBlockSubsetType}
                value={buildingBlockSubsetType}
                options={subsetOptions}
                inPortal
              />
            </div>
          )}
          {buildingBlockSubsetType !== BuildingBlockSubsetTypes.FROM_DATA && (
            <ButtonComponent
              css={{
                gridArea: 'goToMap',
                width: !SUBSET_TYPE_SELECTION_ENABLED ? 140 : undefined,
                justifySelf: SUBSET_TYPE_SELECTION_ENABLED ? 'stretch' : undefined,
              }}
              buttonStyle={ButtonStyle.Secondary}
              text={selectedBoundaryIds?.length ? t('Edit Selection') : t('Go To Map')}
              onClick={onSelectSubsetFromMap}
            />
          )}
        </>
      )}

      {selectedBoundaryIds?.length ? (
        <span css={{ gridArea: 'selectedBlocks' }}>
          {t('boundaryAI.{{count}} blocks selected', { count: selectedBoundaryIds.length })}
        </span>
      ) : null}

      <ContinueButton
        css={{ gridArea: 'continue', justifySelf: 'end' }}
        label={t('Continue')}
        onSubmitClick={onContinue}
        disabled={isSubmitDisabled}
      />
    </div>
  );
};
